'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Crest Have Microplastics? All 50 Formulas Checked (2026)"
      author="MicroPlastics Research Desk"
      date="August 2, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does Crest have microplastics: all 50 Crest toothpaste formulas audited against the ingredient labels Procter & Gamble files with the FDA"
    >
      <BlogLastUpdated iso="2026-08-02" formatted="August 2, 2026" />

      <BlogTLDR>
        <strong>No plastic beads, and it depends entirely on which Crest you
        buy.</strong>{' '}
        We read all <strong>50 Crest toothpaste labels that Procter &amp; Gamble
        files with the FDA</strong>. The synthetic polymer follows the{' '}
        <em>product line</em>, not the brand:{' '}
        <strong>poloxamer 407 in 21 formulas</strong> (the Scope, Active Foam and
        3D White Advanced whitening pastes), <strong>carbomer in 14</strong>{' '}
        (Cavity Protection, the classic 3D White pastes, and{' '}
        <strong>every single kids&apos; formula</strong>), and{' '}
        <strong>15 with neither</strong>. Notably, Crest uses{' '}
        <strong>no PEG at all</strong>, which is the opposite of{' '}
        <Link href="/blog/does-sensodyne-have-microplastics" className="text-forest underline">Sensodyne</Link>.
        If you want a polymer-free Crest, the <strong>Pro-Health</strong> and{' '}
        <strong>Clean Breath</strong> lines are the ones to reach for.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>We checked <strong>P&amp;G&apos;s own FDA-filed labels</strong> for all 50 Crest toothpastes, not a third-party ingredient database.</>,
          <><strong>Poloxamer 407 appears in 21 formulas</strong>, concentrated in the whitening and foaming lines: Crest + Scope, Premium Plus Active Foam, and 3D White Advanced.</>,
          <><strong>Carbomer appears in 14</strong>, including Cavity Protection, Fresh &amp; White, and <strong>every kids&apos; formula we found</strong>: Sparkle Fun, Bubblegum, Strawberry and Strawberry Rush.</>,
          <><strong>15 formulas contain neither.</strong> The Pro-Health line, the Clean Breath line and 3D White Deep Stain Remover are the clean ones.</>,
          <><strong>No Crest toothpaste uses PEG</strong>, the polymer that dominates Sensodyne. The two brands made opposite formulation choices.</>,
          <>Solid plastic microbeads are in <strong>none</strong> of them. US manufacturing ended in 2018.</>,
          <><strong>Cellulose gum, xanthan gum and carrageenan are not plastics.</strong> They appear all over these labels and lists that flag them are inflating the problem.</>,
        ]}
      />

      <InlineGuessReveal
        source="crest_polymer_count"
        question="Crest files 50 toothpaste labels with the FDA. How many contain a synthetic polymer?"
        options={[
          { label: 'None' },
          { label: 'About 10' },
          { label: '35 of 50', correct: true },
          { label: 'All 50' },
        ]}
        answer="35 of 50"
        answerNote={
          <>
            Poloxamer 407 in 21, carbomer in 14, and 15 with neither. None of
            them contain plastic microbeads, which were banned in 2018. The
            polymer follows the <em>product line</em>, not the brand, so which
            tube you pick decides the answer.
          </>
        }
        ctaText="Your tube will not tell you which of the three groups it is in. The app reads the label for you."
        ctaLabel="Scan your toothpaste"
      />

      <RiskQuizInlineNudge source="blog_inline_crest" />

      <KeyStats
        heading="Crest toothpaste and synthetic polymers: the label tally"
        stats={[
          { value: '21 of 50', label: 'formulas listing poloxamer 407', context: 'concentrated in the Scope, Active Foam and 3D White Advanced lines', source: "P&G FDA labels via DailyMed", sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
          { value: '14 of 50', label: 'formulas listing carbomer', context: 'Cavity Protection, classic 3D White, and every kids formula', source: "P&G FDA labels via DailyMed", sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
          { value: '15 of 50', label: 'formulas with neither polymer', context: 'the Pro-Health and Clean Breath lines, plus 3D White Deep Stain Remover', source: "P&G FDA labels via DailyMed", sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
          { value: 'Zero', label: 'Crest toothpastes using PEG', context: 'the polymer that appears in 11 of 14 Sensodyne formulas', source: "P&G FDA labels via DailyMed", sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        Crest contains no solid plastic microbeads. Those were banned under the
        Microbead-Free Waters Act and US toothpaste manufacturing ended in{' '}
        <strong>2018</strong>. Anyone still telling you Crest has scrubbing beads
        is recycling a decade-old story.
      </p>
      <p>
        What Crest does contain, in most but not all formulas, is a{' '}
        <strong>water-soluble synthetic polymer</strong>, either poloxamer 407 or
        carbomer. These are petroleum-derived plastics in the chemical sense, but
        they dissolve rather than existing as particles, which is a meaningful
        difference we come back to below.
      </p>
      <p>
        The useful finding is that Crest is not one answer. It is three. Which
        polymer you get, or whether you get one at all, is decided by which
        product line you pick up off the shelf.
      </p>

      <h2>How we checked</h2>
      <p>
        Fluoride toothpaste is regulated in the US as an over-the-counter drug,
        so the manufacturer must file a full ingredient label with the FDA. We
        pulled <strong>all 50 Crest dentifrice labels filed by The Procter &amp;
        Gamble Manufacturing Company</strong> in the FDA&apos;s DailyMed system
        and read the inactive-ingredient lists directly. That is P&amp;G&apos;s
        own disclosure, not a scraped database. A handful of products appear more
        than once in the filings (different pack configurations), which we have
        left in rather than quietly adjusting the denominator.
      </p>

      <h2>Crest by product line</h2>

      <BlogComparisonTable
        caption="Crest toothpaste lines by synthetic polymer, from P&G's FDA-filed inactive ingredient lists (checked August 2026)"
        columns={[
          { key: 'line', label: 'Product line' },
          { key: 'polymer', label: 'Synthetic polymer' },
          { key: 'examples', label: 'Examples' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { line: 'Pro-Health', polymer: 'None', examples: 'Advanced Protection, Advanced Bacteria Protection, Sensitivity Relief', verdict: 'Cleanest' },
          { line: 'Clean Breath', polymer: 'None', examples: 'Peppermint, Spearmint, Smooth Mint', verdict: 'Cleanest' },
          { line: '3D White Deep Stain Remover', polymer: 'None', examples: 'Ultra White, Enamel Strength, Brilliant White', verdict: 'Cleanest' },
          { line: '3D White Stain Eraser', polymer: 'None (cellulose gum only)', examples: 'Icy Clean Mint, Mint Splash, Polishing', verdict: 'Clean' },
          { line: 'Crest + Scope / Premium Plus Active Foam', polymer: 'Poloxamer 407', examples: 'Outlast Mint, Minty Fresh, Active Foam Whitening', verdict: 'Contains polymer' },
          { line: '3D White Advanced (stain-blasting foam)', polymer: 'Poloxamer 407', examples: 'Radiant Mint, Arctic Fresh, Charcoal, Glamorous White', verdict: 'Contains polymer' },
          { line: 'Cavity Protection / Fresh & White', polymer: 'Carbomer', examples: 'Cavity Protection Regular, Fresh & White', verdict: 'Contains polymer' },
          { line: "Kids' Crest (all flavours found)", polymer: 'Carbomer', examples: 'Sparkle Fun, Bubblegum, Strawberry, Strawberry Rush', verdict: 'Contains polymer' },
        ]}
      />

      <h2>The kids&apos; formula finding</h2>
      <p>
        Worth pulling out on its own: <strong>every Crest kids&apos; toothpaste
        we found in the FDA filings lists carbomer</strong>, across Sparkle Fun,
        Bubblegum, Strawberry and Strawberry Rush. There is no polymer-free
        kids&apos; Crest in the current filings.
      </p>
      <p>
        We want to be careful about what that does and does not mean. Carbomer is
        a crosslinked polyacrylic acid used as a thickener, it is water-soluble,
        and there is no published measurement of particle release from a
        children&apos;s toothpaste. It is not evidence of harm. But if you are a
        parent who has been buying kids&apos; Crest specifically to avoid
        additives, the polymer is there and it is worth knowing, because the
        adult Pro-Health line does not have it.
      </p>

      <h2>What these ingredients are</h2>
      <ul>
        <li>
          <strong>Poloxamer 407.</strong> A synthetic block copolymer built from
          polyethylene oxide and polypropylene oxide units. In Crest it is doing
          the foaming and surfactant work, which is why it clusters in the
          Scope and Active Foam lines. Water-soluble.
        </li>
        <li>
          <strong>Carbomer.</strong> Crosslinked polyacrylic acid, a synthetic
          thickener that gives paste its body. Also water-soluble.
        </li>
        <li>
          <strong>Not plastics:</strong> cellulose gum (plant cellulose), xanthan
          gum (fermented sugar) and carrageenan (seaweed). All three are common
          across these labels and all three are natural polysaccharides. Lists
          that count them are padding.
        </li>
      </ul>

      <h2>Do these count as microplastics?</h2>
      <p>
        This is a genuine definitional disagreement and we would rather show it
        than pick a side for you.
      </p>
      <p>
        Under the definition most researchers use, a microplastic is a{' '}
        <strong>solid, insoluble plastic particle</strong> below 5&nbsp;mm.
        Poloxamer and carbomer dissolve, so on that definition Crest contains no
        microplastics at all. Under the broader definition used by groups like
        Beat the Microbead, any synthetic petroleum-derived polymer in a
        personal-care product counts as a plastic ingredient, because it still
        goes down the drain and still comes from fossil feedstock. On that
        reading, 35 of the 50 formulas qualify.
      </p>
      <p>
        What is <em>not</em> supportable either way is the claim that brushing
        with Crest releases a countable number of microplastic particles into
        your mouth. No lab has published a particle measurement for any Crest
        formula. Any specific number you see attributed to Crest is invented.
      </p>

      <h2>Which Crest to buy instead</h2>
      <ul>
        <li>
          <strong>Stay in the brand, change the line.</strong> If you want Crest
          without a synthetic polymer, <strong>Crest Pro-Health</strong>{' '}
          (Advanced Protection, Advanced Bacteria Protection, or Sensitivity
          Relief) and the <strong>Clean Breath</strong> range carry neither
          poloxamer nor carbomer. That is a same-shelf, same-price swap.
        </li>
        <li>
          <strong>Skip the foam.</strong> The reliable tell across the whole
          brand is foaming and whitening claims. &ldquo;Active Foam,&rdquo;
          &ldquo;stain-blasting foam&rdquo; and the Scope combinations are where
          poloxamer 407 lives.
        </li>
        <li>
          <strong>For kids, there is no in-brand fix.</strong> Every kids&apos;
          Crest in the filings uses carbomer, so a polymer-free children&apos;s
          paste means leaving the brand. See our{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">microplastic-free toothpaste picks</Link>.
        </li>
        <li>
          <strong>Don&apos;t trade away fluoride for this.</strong> These
          polymers are soluble and unmeasured; cavities are neither. Treat this
          as a which-tube question, not a whether-to-brush question.
        </li>
        <li>
          <strong>Compare with the other big brand:</strong>{' '}
          <Link href="/blog/does-sensodyne-have-microplastics" className="text-forest underline">Sensodyne&apos;s 14 formulas</Link>{' '}
          use PEG-8 instead, and the category picture is in{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">microplastics in toothpaste</Link>.
        </li>
      </ul>

      <BlogAppCTA
        headline="Scan the tube in your bathroom"
        sub="Point the MicroPlastics app at your Crest, Sensodyne or any toothpaste to see which synthetic polymers are on the label and which line to switch to."
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Crest have microplastics?',
            answer: 'No solid plastic microbeads, those were banned and US manufacturing ended in 2018. Of the 50 Crest toothpaste labels P&G files with the FDA, 21 list poloxamer 407 and 14 list carbomer, both water-soluble synthetic polymers. 15 formulas contain neither. Because these dissolve rather than existing as particles, they are not microplastics under a strict scientific definition, though campaign groups count them as plastic ingredients.',
          },
          {
            question: 'Which Crest toothpaste has no synthetic polymers?',
            answer: 'The Crest Pro-Health line (Advanced Protection, Advanced Bacteria Protection, Sensitivity Relief), the Clean Breath line (Peppermint, Spearmint, Smooth Mint) and 3D White Deep Stain Remover list neither poloxamer nor carbomer. The 3D White Stain Eraser range uses only cellulose gum, which is plant-derived rather than synthetic.',
          },
          {
            question: 'Does kids Crest toothpaste have plastic in it?',
            answer: 'Every Crest kids formula in the FDA filings, including Sparkle Fun, Bubblegum, Strawberry and Strawberry Rush, lists carbomer, a synthetic crosslinked polyacrylic acid used as a thickener. It is water-soluble and there is no published measurement of particle release from a childrens toothpaste, so this is not evidence of harm. But there is no polymer-free kids Crest, so avoiding it means changing brand.',
          },
          {
            question: 'Is Crest or Sensodyne better for microplastics?',
            answer: 'They use different polymers, and both have clean options. Crest uses poloxamer 407 (21 of 50 formulas) and carbomer (14), and no PEG anywhere. Sensodyne uses PEG-8 in 11 of its 14 formulas. Crest has more polymer-free choices in absolute terms, including the whole Pro-Health line; Sensodyne has two, Cavity & Sensitivity and Pronamel Gentle Whitening Advanced Plus. Neither contains microbeads.',
          },
          {
            question: 'Is poloxamer 407 in toothpaste safe?',
            answer: 'Poloxamer 407 is a synthetic block copolymer used as a foaming agent and surfactant, and it is water-soluble rather than a solid particle. It is widely used in pharmaceuticals as well as toothpaste. There is no evidence it releases microplastic particles while brushing, and no lab has published a particle count for a Crest formula. The objection to it is that it is a fossil-derived polymer entering wastewater, not a demonstrated health risk at these levels.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'The Procter & Gamble Manufacturing Company', year: 2026, title: 'Crest structured product labels (all 50 US dentifrice filings, inactive ingredient listings)', publisher: 'FDA DailyMed', url: 'https://dailymed.nlm.nih.gov/dailymed/search.cfm?labeltype=all&query=crest' },
          { authors: 'US Food and Drug Administration', year: 2015, title: 'The Microbead-Free Waters Act: FAQs (manufacturing ban dates for rinse-off cosmetics and toothpaste)', publisher: 'FDA', url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs' },
          { authors: 'Plastic Soup Foundation', year: 2026, title: 'Myth buster: toothpaste still contains plastic ingredients', publisher: 'Beat the Microbead', url: 'https://www.beatthemicrobead.org/myth-buster-toothpaste-still-contains-plastic-ingredients/' },
        ]}
      />
    </BlogPostLayout>
  )
}
